/**
 * Markdown to Rich Blocks Converter
 *
 * Converts plain markdown to Rich Chat blocks format.
 * Used for OLD messages that were saved before Rich Chat was implemented.
 *
 * @module utils/markdownToBlocks
 */

/**
 * Parse markdown content into Rich Chat blocks
 * @param {string} markdown - Raw markdown content
 * @returns {Array|null} Array of Rich Chat blocks or null
 */
export const parseMarkdownToBlocks = markdown => {
  if (!markdown || typeof markdown !== 'string') return null;

  const blocks = [];
  const lines = markdown.split('\n');
  let currentList = null;
  let currentCodeBlock = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Handle code blocks
    if (trimmedLine.startsWith('```')) {
      if (currentCodeBlock) {
        // End of code block
        blocks.push({
          type: 'code',
          language: currentCodeBlock.language,
          content: currentCodeBlock.content.trim(),
          allowCopy: true,
        });
        currentCodeBlock = null;
        continue;
      } else {
        // Start of code block
        const langMatch = trimmedLine.match(/```(\w+)/);
        currentCodeBlock = {
          language: langMatch ? langMatch[1] : 'text',
          content: '',
        };
        continue;
      }
    }

    if (currentCodeBlock) {
      currentCodeBlock.content += line + '\n';
      continue;
    }

    // Handle lists
    if (trimmedLine.match(/^(\*|-|\+)\s/) || trimmedLine.match(/^\d+\.\s/)) {
      if (!currentList) {
        currentList = {
          type: trimmedLine.match(/^\d+\.\s/) ? 'numbered_list' : 'bullet_list',
          items: [],
        };
      }
      currentList.items.push(trimmedLine.replace(/^(\*|-|\+|\d+\.)\s/, ''));
      continue;
    } else if (currentList) {
      blocks.push(currentList);
      currentList = null;
    }

    // Handle headers
    const headerMatch = trimmedLine.match(/^(#+)\s(.+)/);
    if (headerMatch) {
      blocks.push({
        type: 'section_header',
        text: headerMatch[2],
        level: Math.min(3, headerMatch[1].length),
      });
      continue;
    }

    // Handle horizontal rule
    if (trimmedLine === '---' || trimmedLine === '***' || trimmedLine === '___') {
      blocks.push({ type: 'divider' });
      continue;
    }

    // Handle blockquotes as callouts
    if (trimmedLine.startsWith('>')) {
      blocks.push({
        type: 'callout',
        variant: 'info',
        text: trimmedLine.substring(1).trim(),
      });
      continue;
    }

    // Handle paragraphs (non-empty lines)
    if (trimmedLine.length > 0) {
      blocks.push({ type: 'paragraph', text: trimmedLine });
    }
  }

  // Push any pending blocks
  if (currentCodeBlock) {
    blocks.push({
      type: 'code',
      language: currentCodeBlock.language,
      content: currentCodeBlock.content.trim(),
      allowCopy: true,
    });
  }
  if (currentList) {
    blocks.push(currentList);
  }

  return blocks.length > 0 ? blocks : null;
};

/**
 * Convert plain markdown to Rich Chat blocks
 * Handles JSON cleanup and extraction for legacy messages
 *
 * @param {string} markdown - Raw markdown or JSON content
 * @returns {Array|null} Array of Rich Chat blocks or null
 */
export const convertMarkdownToRichBlocks = markdown => {
  if (!markdown || typeof markdown !== 'string') return null;

  let content = markdown.trim();

  // Remove trailing JSON blocks from mixed content
  // Sometimes old messages have markdown content followed by raw JSON
  const jsonStartPatterns = [
    /\n\s*json\s*\n\s*\{/i, // "json\n{" code block start
    /\n\s*```json\s*\n/i, // "```json\n" code block
    /\n\s*\{\s*\n\s*"answer"/, // newline + { + "answer"
    /\n\s*\{\s*"answer"/, // newline + {"answer"
  ];

  for (const pattern of jsonStartPatterns) {
    const match = content.match(pattern);
    if (match && match.index && match.index > 100) {
      // There's meaningful content before the JSON - strip the JSON part
      content = content.substring(0, match.index).trim();
      console.log(`🧹 [Rich Chat] Stripped trailing JSON from mixed content`);
      break;
    }
  }

  // If content STARTS with JSON, extract the answer
  if (content.startsWith('{') || content.startsWith('[')) {
    // First try to extract answer using regex (works even with truncated/malformed JSON)
    const answerMatch = content.match(/"answer"\s*:\s*"([\s\S]*?)(?:"\s*,\s*"|"\s*}|$)/);
    if (answerMatch && answerMatch[1]) {
      // Unescape the extracted content
      const extractedAnswer = answerMatch[1]
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\r')
        .replace(/\\t/g, '\t')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');

      if (extractedAnswer.length > 50) {
        console.log(`🔍 [Rich Chat] Extracted answer from JSON (${extractedAnswer.length} chars)`);
        return parseMarkdownToBlocks(extractedAnswer);
      }
    }

    try {
      const parsed = JSON.parse(content);
      // If it's a valid JSON object with an "answer" field, extract that
      if (parsed.answer && typeof parsed.answer === 'string') {
        return parseMarkdownToBlocks(parsed.answer);
      }
      // If it's already blocks, return them
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].type) {
        return parsed;
      }
      // If it has key structured data, render nicely
      if (parsed.summary || parsed.keyPoints) {
        const blocks = [];
        if (parsed.summary) {
          blocks.push({ type: 'callout', variant: 'info', title: 'Summary', text: parsed.summary });
        }
        if (parsed.keyPoints && Array.isArray(parsed.keyPoints)) {
          blocks.push({ type: 'section_header', text: 'Key Points', level: 2 });
          blocks.push({ type: 'bullet_list', items: parsed.keyPoints });
        }
        if (blocks.length > 0) return blocks;
      }
      // For other JSON, try to create a readable format instead of raw code block
      if (typeof parsed === 'object') {
        const blocks = [];
        Object.entries(parsed).forEach(([key, value]) => {
          if (typeof value === 'string' && value.length > 20) {
            blocks.push({
              type: 'section_header',
              text: key.replace(/([A-Z])/g, ' $1').trim(),
              level: 3,
            });
            blocks.push({ type: 'paragraph', text: value });
          }
        });
        if (blocks.length > 0) return blocks;
      }
    } catch (e) {
      // JSON parse failed - content will be parsed as markdown below
      console.log(`⚠️ [Rich Chat] JSON parse failed, treating as markdown`);
    }
  }

  return parseMarkdownToBlocks(content);
};

export default {
  convertMarkdownToRichBlocks,
  parseMarkdownToBlocks,
};
